import React, { 
  useState, 
  useRef, 
  useEffect, 
  useCallback, 
  useMemo, 
  memo 
} from 'react';
import './App.css';
import ChatInput from './components/chat/ChatInput';
import MessageList from './components/chat/MessageList';
import CreateSessionDialog from './components/dialogs/CreateSessionDialog';
import DeleteConfirmDialog from './components/dialogs/DeleteConfirmDialog';
import ScriptGeneratorDialog from './components/dialogs/ScriptGeneratorDialog';
import SettingsDialog from './components/dialogs/SettingsDialog';
import Toolbar from './components/toolbar/Toolbar';
import Sidebar from './components/sidebar/Sidebar';
import { fetchChatCompletion, processStreamResponse } from './utils/api';
import { 
  loadSessions, 
  saveSession, 
  loadCurrentSessionId,
  saveCurrentSessionId,
  loadApiKey,
  saveApiKey,
  loadTheme
} from './utils/storage';
import { applyTheme } from './utils/theme';

function App() {
  const [sessions, setSessions] = useState(loadSessions);
  const [currentSessionId, setCurrentSessionId] = useState(loadCurrentSessionId);
  const [isLoading, setIsLoading] = useState(false);
  const [currentAssistantMessage, setCurrentAssistantMessage] = useState('');
  const messagesEndRef = useRef(null);
  const [apiKey, setApiKey] = useState(loadApiKey);
  const [errorMessage, setErrorMessage] = useState('');
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    const theme = loadTheme();
    applyTheme(theme);
  }, []);

  useEffect(() => {
    if (apiKey) {
      saveApiKey(apiKey);
    }
  }, [apiKey]);

  useEffect(() => {
    saveSession(sessions);
    saveCurrentSessionId(currentSessionId);
  }, [sessions, currentSessionId]);

  const currentSession = useMemo(() => {
    return sessions.find(s => s.id === currentSessionId) || sessions[0];
  }, [sessions, currentSessionId]);

  const getCurrentSession = useMemo(() => () => {
    return sessions.find(s => s.id === currentSessionId) || sessions[0];
  }, [sessions, currentSessionId]);

  const handleSubmit = useCallback(async (inputText) => {
    if (!apiKey) {
      alert('请先设置 API Key');
      setShowSettings(true);
      return;
    }

    setErrorMessage('');
    const userMessage = { role: 'user', content: inputText };
    setSessions(prev => prev.map(s => 
      s.id === currentSessionId 
        ? { ...s, messages: [...s.messages, userMessage] }
        : s
    ));
    setIsLoading(true);
    setCurrentAssistantMessage('');

    try {
      const response = await fetchChatCompletion(
        [...getCurrentSession().messages, userMessage],
        apiKey
      );

      const fullMessage = await processStreamResponse(
        response,
        setCurrentAssistantMessage
      );

      setSessions(prev => prev.map(s => 
        s.id === currentSessionId 
          ? { ...s, messages: [...s.messages, { role: 'assistant', content: fullMessage }] }
          : s
      ));
    } catch (error) {
      console.error('Error:', error);
      setErrorMessage(error.message || '生成失败，请重试');
    } finally {
      setIsLoading(false);
      setCurrentAssistantMessage('');
    }
  }, [apiKey, currentSessionId, getCurrentSession]);

  const handleRetry = useCallback(() => {
    const currentSession = getCurrentSession();
    const lastUserMessage = currentSession.messages[currentSession.messages.length - 1];
    if (lastUserMessage && lastUserMessage.role === 'user') {
      handleSubmit(lastUserMessage.content);
    }
  }, [handleSubmit, getCurrentSession]);

  const currentMessages = useMemo(() => 
    getCurrentSession().messages, 
    [getCurrentSession]
  );

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  const renameSession = useCallback((sessionId, newName) => {
    setSessions(prev => prev.map(s => 
      s.id === sessionId ? { ...s, name: newName } : s
    ));
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [currentMessages, currentAssistantMessage, scrollToBottom]);

  const createNewSession = (name) => {
    const newSession = {
      id: Date.now().toString(),
      name: name,
      messages: []
    };
    setSessions(prev => [...prev, newSession]);
    setCurrentSessionId(newSession.id);
    setShowSessionsList(false);
  };

  const [deleteDialogState, setDeleteDialogState] = useState({ 
    show: false, 
    id: null, 
    name: '' 
  });

  const handleDeleteSession = (sessionId, sessionName) => {
    setDeleteDialogState({ 
      show: true, 
      id: sessionId, 
      name: sessionName 
    });
  };

  const [showSessionsList, setShowSessionsList] = useState(false);

  const toggleSessionsList = useCallback(() => {
    setShowSessionsList(prev => !prev);
  }, []);

  const handleSessionClick = useCallback((sessionId) => {
    setCurrentSessionId(sessionId);
    setShowSessionsList(false);
  }, []);

  const currentSessionName = useMemo(() => {
    return currentSession?.name || '新对话';
  }, [currentSession]);

  const [showCreateDialog, setShowCreateDialog] = useState(false);

  const [showScriptDialog, setShowScriptDialog] = useState(false);

  return (
    <div className="app">
      <Sidebar 
        sessions={sessions}
        currentSessionId={currentSessionId}
        showSessionsList={showSessionsList}
        onNewChat={() => setShowCreateDialog(true)}
        onShowSettings={() => setShowSettings(true)}
        onSessionClick={handleSessionClick}
        onSessionDelete={handleDeleteSession}
        onSessionRename={renameSession}
        currentSessionName={currentSessionName}
        onToggleList={toggleSessionsList}
      />

      <div className="chat-container">
        <Toolbar 
          onGenerateScript={() => setShowScriptDialog(true)}
        />
        <MessageList 
          messages={currentSession.messages}
          currentMessage={currentAssistantMessage}
          isLoading={isLoading}
          onRetry={handleRetry}
          errorMessage={errorMessage}
        />
        <div className="chat-bottom">
          <ChatInput 
            onSubmit={handleSubmit}
            disabled={isLoading}
          />
          <div className="beian">
            <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer">
              蒙 ICP 备 12231
            </a>
          </div>
        </div>
      </div>

      <CreateSessionDialog
        isOpen={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        onConfirm={createNewSession}
      />

      <ScriptGeneratorDialog
        isOpen={showScriptDialog}
        onClose={() => setShowScriptDialog(false)}
        onSubmit={handleSubmit}
      />

      <DeleteConfirmDialog
        isOpen={deleteDialogState.show}
        onClose={() => setDeleteDialogState({ show: false, id: null, name: '' })}
        onConfirm={() => {
          setSessions(prev => prev.filter(s => s.id !== deleteDialogState.id));
          if (currentSessionId === deleteDialogState.id) {
            setCurrentSessionId(sessions[0].id);
          }
          setDeleteDialogState({ show: false, id: null, name: '' });
        }}
        sessionName={deleteDialogState.name}
      />

      <SettingsDialog
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        apiKey={apiKey}
        onApiKeyChange={setApiKey}
      />
    </div>
  );
}

export default memo(App); 